import moduleSettings from '../Utils/settings.js'
import { isMobile } from '../Utils/viewport.js'
import { debounce, throttle } from "throttle-debounce"

class PageNav {
	constructor(opts, el) {
		this.el = el // .PageNav
		this.options = opts

		this.mobileMenuButton = el.querySelector('#mobile-menu')
		this.primaryNav = el.querySelector('#primary')
		this.menuOptions = el.querySelectorAll('#nav-main > li.has-second-level')
		this.currScrollDir = 0
		this.aboutToOpen = null // keep track of which will be opened soon, so we can reset if we hover over another menu item
		this.currentlyOpen = null // currently opened menu item. Used to close it when another menu item is hovered over
		this.timer = null // timer to keep track of it we are about to open a menu item, so we can reset and start again if we hover over another menu item
		this.persistOpen = false // keep track of if we should keep the menu open or not
		
		this.init()
	}

	init() {
		// First of all, check if we don't have self identity. If we don't, we need to fix our css var
		const root = document.documentElement
		// check if the element `.utility-menu` exists inside this.el
		if (this.el.querySelector('.utility-menu')) {
			root.classList.add('no-transition');
			root.style.setProperty('--self-identity-selector-height', '60px')
			root.classList.remove('no-transition');
        }
		// Start resize event listener to reinitialize table if the window is resized
		jAwn.on(window, `resize.PageNav`, debounce(300, this.onResize.bind(this)))

		// Start scroll event listener to show/hide menu based on scroll direction. Throttled to 100 milliseconds.
		jAwn.on(window, "scroll.PageNav", throttle(100, this.onScroll.bind(this)));

		if (isMobile()) {
			// Start mobile event listeners
			this.mobileEvents()
		} else {
			this.desktopEvents()
		}

		// set all the tabindexes to -1 so they are not focusable in submenus, because we know we don't have any open right now
		this.menuOptions.forEach(menuOption => {
            this.setTabIndexes(menuOption, -1)
        })
	}

	onScroll() {
		const currScrollPos = window.scrollY

		if (isMobile() || (this.el.classList.contains("cease") && currScrollPos > 0)) {
			return;
		}

		const headerNavHeight = this.el.offsetHeight

		if ((this.currScrollDir < currScrollPos) && (currScrollPos > headerNavHeight)) {
			this.el.classList.add('PageNav-isHidden')
			const active = this.el.querySelector('.has-second-level.is-open')
			if (active) {
				this.persistOpen = false
				jAwn.trigger(active, 'mouseleave.PageNav')
			}
		} else {
			this.el.classList.remove('PageNav-isHidden')
		}

		this.currScrollDir = currScrollPos;
	}

	mobileEvents() {
		// Start click event listener to show/hide mobile menu
		jAwn.on(this.mobileMenuButton, "click.PageNav", this.onHamburgerClick.bind(this));
		// Start click event listener to show/hide mobile sub menu
		this.menuOptions.forEach(menuOption => {
            jAwn.on(menuOption, "click.PageNav", this.onMobileMenuOptionClick.bind(this));
		})

		// we need to calculate the height of all the sub menus and store it as a data attribute on the parent li
		this.menuOptions.forEach(menuOption => {
            const subMenu = menuOption.querySelector(':scope > div')
			if (subMenu) {
				menuOption.dataset.subMenuHeight = subMenu.scrollHeight
            }
        })
	}

	// Show/hide mobile menu
	onHamburgerClick(e) {
		e.preventDefault()
		if (this.mobileMenuButton.classList.contains('active')) {
			document.body.classList.remove('nav-open')
			this.mobileMenuButton.classList.remove('active')
			this.primaryNav.classList.remove('active')
		} else {
			document.body.classList.add('nav-open')
			this.mobileMenuButton.classList.add('active')
			this.primaryNav.classList.add('active')
		}
	}

	onMobileMenuOptionClick(e) {
		const menuOption = e.currentTarget
		const subMenu = menuOption.querySelector(':scope > div')
		// set style to the height of the sub menu
		if (menuOption.classList.contains('is-open')) {
			subMenu.style.maxHeight = '0'
		} else {
			subMenu.style.maxHeight = `${menuOption.dataset.subMenuHeight}px`
        }
		menuOption.classList.toggle('is-open')
	}

	desktopEvents() {
		this.menuOptions.forEach(menuOption => {
			jAwn.on(menuOption, "mouseenter.PageNav", this.onMouseEnter.bind(this));
			jAwn.on(menuOption, "mouseleave.PageNav", this.onMouseLeave.bind(this));
			jAwn.on(menuOption, "click.PageNav", this.onDesktopMenuOptionClick.bind(this));
			// set up keyboard listeners for ADA
			jAwn.on(menuOption, "keydown.PageNav", this.onDesktopMenuOptionClick.bind(this));
			// this is only used to check for left/right arrow keys to switch between menu items
			jAwn.on(menuOption, "keyup.PageNav", this.onDesktopMenuOptionSwitch.bind(this))
		})
	}

	onMouseEnter(e) {
		if (this.persistOpen) return // if we are persisting open, don't do anything

		const menuOption = e.currentTarget
		this.aboutToOpen = menuOption
		let timer = this.options.openDelay
		if (this.currentlyOpen) {
			document.body.classList.add(`no-transition`)
			timer = 2
		}
		clearTimeout(this.timer)
        this.timer = null
        this.timer = setTimeout(() => {
			menuOption.classList.add('is-open')
			this.currentlyOpen = menuOption
			setTimeout(() => { document.body.classList.remove(`no-transition`) }, 30)
		}, timer)
	}

	onMouseLeave(e) {
		if (this.persistOpen) return // if we are persisting open, don't do anything
		const menuOption = e.currentTarget
		clearTimeout(this.timer)
		this.timer = null
		// delay just a bit to see if we are going to hover over another menu item
		setTimeout(() => {
			menuOption.classList.remove('is-open')
			if (this.currentlyOpen === menuOption) this.currentlyOpen = null
		}, 2)
			
	}

	onDesktopMenuOptionClick(e) {
		// This will be called from both click and keydown events (enter).
		// If the menu is not open, open it and tell it to persist. We also need to check if any other menu is already opened, and close it first.
		// If menu is open, check if we're persisting. If so, close it, otherwise tell it to persist without doing anything else.

		if (e.type === 'keydown' && e.key !== 'Enter') return

		// if click is anything in the submenu div, don't do anything
		if (e.target?.closest('.has-second-level > div')) return

		if (e.preventDefault) e.preventDefault()

		const menuOption = e.currentTarget
		if (!menuOption.classList.contains('is-open')) {
			if (this.currentlyOpen) {
				this.currentlyOpen.classList.remove('is-open')
				this.currentlyOpen = null
				this.handleOutsideClickAndEscape(null, false)
			}
			menuOption.classList.add('is-open')
			this.currentlyOpen = menuOption
			this.persistOpen = true
			this.setTabIndexes(menuOption, 0)
			this.handleOutsideClickAndEscape(menuOption, true)
		} else {
			if (this.persistOpen) {
				menuOption.classList.remove('is-open')
				this.currentlyOpen = null
				this.persistOpen = false
				this.setTabIndexes(menuOption, -1)
				this.handleOutsideClickAndEscape(null, false)
			} else {
				this.persistOpen = true
				this.handleOutsideClickAndEscape(menuOption, true)
			}
		}
	}

	handleOutsideClickAndEscape(menuOption, turnOn) {
		if (turnOn) {
			setTimeout(() => {
				jAwn.on(document.body, 'keydown.OutsidePageNav', (e) => {
					if (e.key === 'Escape') {
						this.onDesktopMenuOptionClick({ type: 'keydown', key: 'Enter', currentTarget: menuOption })

					}
				})
				jAwn.on(document.body, 'click.OutsidePageNav', (e) => {
					if (!menuOption?.contains(e.target)) {
						this.onDesktopMenuOptionClick({ type: 'click', currentTarget: menuOption })
					}
				})
			}, 5)
		} else {
			jAwn.off(document.body, 'click.OutsidePageNav');
			jAwn.off(document.body, 'keydown.OutsidePageNav');
		}
	}

	onDesktopMenuOptionSwitch(e) {
		// if left / right arrow key is pressed, we should switch between menu items.
		// if opened (this.currentlyOpened === el), we should close the current and open the next one, otherwise just cycle through the options without opening them
		if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return

		const menuOption = e.currentTarget
		const menuOptions = Array.from(this.menuOptions)
		const currentIndex = menuOptions.indexOf(menuOption)
		const wasCurrentlyOpened = this.currentlyOpen !== null
		let nextIndex = (e.key === 'ArrowLeft') ? currentIndex - 1 : currentIndex + 1
		if (nextIndex < 0) nextIndex = menuOptions.length - 1
		if (nextIndex >= menuOptions.length) nextIndex = 0

		const nextMenuOption = menuOptions[nextIndex]
		if (this.currentlyOpen === menuOption) { // if the current menu item is opened, we should close it
			this.onDesktopMenuOptionClick({ type: 'keydown', key: 'Enter', currentTarget: this.currentlyOpen })

		}
		// focus the next menu item
		nextMenuOption.querySelector('a').focus()
		// open the next menu item
		if (wasCurrentlyOpened) this.onDesktopMenuOptionClick({ type: 'keydown', key: 'Enter', currentTarget: nextMenuOption })
    }

	setTabIndexes(el, val) {
		// set tabindex on every focusable element in the child
		const focusable = el.querySelector(':scope > div').querySelectorAll('a, button, input, select, textarea, [tabindex]')
		focusable.forEach(focus => {
            focus.setAttribute('tabindex', val)
        })
	}

	onResize() {
		this.destroy()
		this.init()
    }

	destroy() {
		// Remove the resize and scroll event listener
		jAwn.off(window, "resize.PageNav");
        jAwn.off(window, "scroll.PageNav");

		// Remove all desktop event listeners that we might have added
		this.menuOptions.forEach(menuOption => {
            jAwn.off(menuOption, "mouseenter.PageNav");
			jAwn.off(menuOption, "mouseleave.PageNav");
			jAwn.off(menuOption, "click.PageNav");
			jAwn.off(menuOption, "keydown.PageNav");
			jAwn.off(menuOption, "keyup.PageNav");
		})
		this.handleOutsideClickAndEscape(null, false)

		// Remove all mobile event listeners that we might have added
        jAwn.off(this.mobileMenuButton, "click.PageNav");
        this.menuOptions.forEach(menuOption => {
            jAwn.off(menuOption, "click.PageNav");
		})

		// reset all max-heights on sub menus used in mobile, in case we go to desktop
		this.menuOptions.forEach(menuOption => {
            const subMenu = menuOption.querySelector(':scope > div')
            if (subMenu) {
                subMenu.style.maxHeight = ''
            }
        })

		// remove classes added
		document.body.classList.remove(`nav-open`)
    }
}

export const init = (opts, el) => {
	const settings = moduleSettings.set({
		openDelay: 500
	}, opts)

	new PageNav(settings, el)
}